/**
 * planKit - w2_16. Shared pieces for the Worthly 2.0 plan pages (Yearly costs,
 * Debt payoff): a full page that sits on top of Home with swipe back, a
 * slide-up sheet, form fields, buttons and money helpers.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSwipeBack } from "../../utils/swipeBack";
import { formatCurrency, formatCurrencyWhole, getCurrentCurrency } from "../../utils/formatters";

export const K = {
  page: '#F4F1FB', card: '#FFFFFF', ink: '#26252E', soft: '#8B879A', faint: '#ABA7B8', line: '#EEEAF6', field: '#F7F5FB', track: '#F0EDF5', btn: '#1F1E24',
  lil: '#ECE6FA', lilT: '#7A5DD3', lilBar: '#B9A6F0', lilDot: '#8E74E3',
  mint: '#DFF2E7', mintT: '#2F8756', mintBar: '#9ED9B6',
  blue: '#DFE9F9', blueT: '#3C6DB5', blueBar: '#A9C6F0',
  pink: '#FAE4ED', pinkT: '#C04B7C', pinkBar: '#F2AFC8',
  butter: '#FCF3D2', butterT: '#8A7A3C', butterBar: '#F3DE8E',
};
export const TINTS = [
  { bg: K.blue, ink: K.blueT, bar: K.blueBar },
  { bg: K.pink, ink: K.pinkT, bar: K.pinkBar },
  { bg: K.lil, ink: K.lilT, bar: K.lilBar },
  { bg: K.mint, ink: K.mintT, bar: K.mintBar },
  { bg: K.butter, ink: K.butterT, bar: K.butterBar },
];
export const tintAt = (i) => TINTS[((i % TINTS.length) + TINTS.length) % TINTS.length];

export const num = (v) => {
  const n = parseFloat(String(v == null ? '' : v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
export const clean = (v) => String(v).replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
export const toText = (v) => (v == null || v === '' || num(v) === 0 ? '' : String(Math.round(num(v) * 100) / 100));
// $1,200 when it's whole dollars, $1,200.50 when it isn't.
export const money = (v) => (Math.abs(v - Math.round(v)) < 0.005 ? formatCurrencyWhole(Math.round(v)) : formatCurrency(v));
export const wholeMoney = (v) => formatCurrencyWhole(Math.round(v));
export const currencySymbol = () => (getCurrentCurrency() || {}).symbol || '$';

export const dayKey = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0') + '-' + String(x.getDate()).padStart(2, '0');
};
export const parseDay = (k) => new Date(String(k).slice(0, 10) + 'T12:00:00');
export const shortDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
export const monthYear = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

/** A full page on top of Home. Swipe from the left edge, or tap ‹, to go back. */
export function PlanPage({ onBack, title, sub, right, children, testid }) {
  const ref = useRef(null);
  useSwipeBack(() => ref.current, () => { if (onBack) onBack(); }, !!onBack);
  return (
    <div
      ref={ref}
      data-swipe-layer="1"
      data-testid={testid}
      style={{ position: 'fixed', inset: 0, zIndex: 55, background: K.page, overflowY: 'auto', overscrollBehavior: 'contain', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 120px)' }}
    >
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '14px 16px 0' }}>
        <div className="flex items-center justify-between" style={{ minHeight: 34 }}>
          {onBack ? (
            <button type="button" aria-label="Back" onClick={onBack} className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 999, border: 0, background: 'rgba(122,93,211,0.08)', color: '#6E6A7C', fontSize: 20, lineHeight: 1, paddingBottom: 2 }}>‹</button>
          ) : <span />}
          {right || null}
        </div>
        {title && <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.9px', color: K.ink, padding: '14px 4px 0', lineHeight: 1.15 }}>{title}</div>}
        {sub && <div style={{ fontSize: 14, color: K.soft, padding: '5px 4px 0', lineHeight: 1.45 }}>{sub}</div>}
        {children}
      </div>
    </div>
  );
}

/** A slide-up sheet. Drag the handle down, tap outside or ✕ to close. */
export function PlanSheet({ title, onClose, children, z = 70 }) {
  const ref = useRef(null);
  const [kb, setKb] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let off = null;
    const onIn = (e) => {
      if (!/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      clearTimeout(off);
      setKb(true);
      setTimeout(() => { try { e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { /* older WebKit */ } }, 320);
    };
    const onOut = () => { clearTimeout(off); off = setTimeout(() => { const a = document.activeElement; if (!(a && el.contains(a) && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) setKb(false); }, 150); };
    el.addEventListener('focusin', onIn);
    el.addEventListener('focusout', onOut);
    return () => { clearTimeout(off); el.removeEventListener('focusin', onIn); el.removeEventListener('focusout', onOut); };
  }, []);
  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: z, touchAction: 'none' }}>
      <div className="absolute" style={{ inset: '-60px 0', background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div
        ref={ref}
        className="absolute bottom-0 left-0 right-0 w-full flex flex-col"
        style={{ background: '#FFFFFF', borderRadius: '28px 28px 0 0', maxHeight: '92vh', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)', boxShadow: '0 -8px 30px rgba(40,30,80,0.10)', touchAction: 'auto', maxWidth: 560, margin: '0 auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex justify-center flex-shrink-0"
          style={{ padding: '10px 0 6px' }}
          onTouchStart={(e) => { e.currentTarget._y = e.touches[0].clientY; }}
          onTouchMove={(e) => { const dy = e.touches[0].clientY - e.currentTarget._y; if (dy > 0) e.currentTarget.parentElement.style.transform = `translateY(${dy}px)`; }}
          onTouchEnd={(e) => {
            const dy = e.changedTouches[0].clientY - e.currentTarget._y;
            const el = e.currentTarget.parentElement;
            if (dy > 60) { el.style.transform = ''; onClose(); } else { el.style.transition = 'transform .2s'; el.style.transform = ''; setTimeout(() => { el.style.transition = ''; }, 200); }
          }}
        >
          <div style={{ width: 40, height: 4, borderRadius: 9, background: '#DDD8E8' }} />
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ padding: '0 18px', paddingBottom: kb ? '45vh' : 8 }}>
          <div className="flex items-center justify-between" style={{ margin: '2px 0 10px', gap: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.3px', color: K.ink }}>{title}</span>
            <button type="button" aria-label="Close" onClick={onClose} className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, border: 0, background: K.page, color: K.soft, fontSize: 14 }}>✕</button>
          </div>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export const inputStyle = { background: K.field, border: 0, borderRadius: 16, padding: '0 14px', height: 50, width: '100%', boxSizing: 'border-box', fontSize: 16, color: K.ink, outline: 'none', fontFamily: 'inherit' };
export const labelStyle = { display: 'block', fontSize: 12.5, color: K.soft, margin: '14px 2px 6px' };
export const helpStyle = { margin: '6px 4px 0', fontSize: 12.5, color: K.soft, lineHeight: 1.45 };

export function Label({ children }) { return <span style={labelStyle}>{children}</span>; }
export function Help({ children }) { return <p style={helpStyle}>{children}</p>; }

/** A money box with the currency symbol in front. */
export function MoneyInput({ value, onChange, placeholder = '0', autoFocus = false }) {
  return (
    <span className="flex items-center" style={{ ...inputStyle, padding: '0 14px' }}>
      <span style={{ fontSize: 16, color: '#B4AFC2', marginRight: 4 }}>{currencySymbol()}</span>
      <input
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(clean(e.target.value))}
        style={{ flex: 1, minWidth: 0, border: 0, background: 'transparent', fontSize: 16, fontWeight: 600, color: K.ink, outline: 'none', fontFamily: 'inherit', padding: 0 }}
      />
    </span>
  );
}

export function BlackButton({ children, onClick, disabled, style }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ display: 'block', width: '100%', border: 0, borderRadius: 999, background: K.btn, color: '#FFFFFF', fontSize: 15.5, fontWeight: 600, padding: 15, marginTop: 16, opacity: disabled ? 0.45 : 1, fontFamily: 'inherit', ...style }}>{children}</button>
  );
}
export function QuietButton({ children, onClick, color, style }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'block', width: '100%', border: 0, background: 'none', color: color || K.soft, fontSize: 14, fontWeight: 600, padding: 10, marginTop: 6, fontFamily: 'inherit', ...style }}>{children}</button>
  );
}
export function Chip({ on, children, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ border: 0, borderRadius: 999, padding: '8px 13px', fontSize: 13.5, fontWeight: 600, background: on ? K.btn : K.field, color: on ? '#FFFFFF' : '#6C6879', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>{children}</button>
  );
}
export function Card({ children, style, onClick, testid }) {
  const s = { display: 'block', width: '100%', textAlign: 'left', background: K.card, borderRadius: 24, padding: 16, marginTop: 12, border: 0, fontFamily: 'inherit', ...style };
  return onClick ? <button type="button" onClick={onClick} style={s} data-testid={testid}>{children}</button> : <div style={s} data-testid={testid}>{children}</div>;
}
export function Note({ tone = 'lil', children, style }) {
  const t = { lil: [K.lil, K.lilT], mint: [K.mint, K.mintT], pink: [K.pink, K.pinkT], blue: [K.blue, K.blueT] }[tone] || [K.lil, K.lilT];
  return <div style={{ background: t[0], color: t[1], borderRadius: 18, padding: '12px 14px', marginTop: 12, fontSize: 13.5, lineHeight: 1.5, ...style }}>{children}</div>;
}
export function Bar({ pct, color, height = 8, style }) {
  return (
    <div style={{ height, borderRadius: 9, background: K.track, overflow: 'hidden', ...style }}>
      <div style={{ width: Math.max(0, Math.min(100, pct)) + '%', height: '100%', borderRadius: 9, background: color }} />
    </div>
  );
}
export function EmojiSquare({ emoji, bg, size = 40 }) {
  return <span className="flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, borderRadius: Math.round(size * 0.34), background: bg, fontSize: Math.round(size * 0.45) }}>{emoji}</span>;
}
